import React, { useState, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import { RiCloseLine, RiCollapseDiagonalLine, RiExpandDiagonalLine, RiRobot2Fill, RiSendPlaneFill, RiUserFill } from "react-icons/ri";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Chart from 'react-apexcharts';
import { chatbotHistory, chatbotInstruction, download_csv } from "../../../modules/chatbotModules.ts";
import {
  chatbotHistorySuccessResponse,
  chatpayload,
} from "../../../modules/types/chatbot.model.ts";
import MessageBubble from "./messageBubble.tsx";
import ReusableTable from "../../../components/common/Table.tsx";
import { useSSE } from "../../../context/SSEContext.tsx";

interface Message {
  id: string | String;
  text: string;
  sender: "user" | "bot";
  isTyping?: boolean;
  waitingForResponse?: boolean;
  result?: any[];
  chartTemplate?: any;
}

interface ColumnDefinition {
  accessorKey: string;
  header: string;
  size: number;
  Cell: ({ cell }: { cell: any }) => React.ReactNode;
}

interface ChatbotProps {
  datasetTableData: any;
  isDataSetView: boolean;
}

const ChatBot: React.FC<ChatbotProps> = ({ datasetTableData, isDataSetView }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [question, setQuestion] = useState<string>("");
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [chatHistory, setChatHistory] = useState<chatbotHistorySuccessResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [chartKey, setChartKey] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [username, setUsername] = useState<string>("");
  const [sidebarWidth, setSidebarWidth] = useState<number>(230);
  const [headerHeight, setHeaderHeight] = useState<number>(72);
  const { subscribeJob } = useSSE();

  // Calculate sidebar width dynamically
  useEffect(() => {
    const calculateSidebarWidth = () => {
      // Try multiple selectors to find the sidebar
      const selectors = [
        '.sidebar',
        '[class*="sidebar"]',
        'nav.sidebar',
        'aside',
        '.nav-sidebar',
        '#sidebar',
        '[id*="sidebar"]',
        'nav[class*="side"]',
        '.side-nav',
        '.sidenav'
      ];

      for (const selector of selectors) {
        const sidebar = document.querySelector(selector);
        if (sidebar) {
          const rect = sidebar.getBoundingClientRect();
          const width = rect.width;
          if (width > 0) {
            setSidebarWidth(width);
            return;
          }
        }
      }
      
      // If no sidebar found, use default
      setSidebarWidth(0); // Set to 0 if no sidebar exists
    };

    const calculateHeaderHeight = () => {
      const selectors = [
        'header',
        '[class*="header"]',
        '.navbar',
        '[class*="navbar"]',
        '.nav-header',
        '#header',
        '[id*="header"]'
      ];

      for (const selector of selectors) {
        const header = document.querySelector(selector);
        if (header) {
          const rect = header.getBoundingClientRect();
          const height = rect.height;
          if (height > 0) {
            setHeaderHeight(height);
            return;
          }
        }
      }
      
      // If no header found, use default
      setHeaderHeight(0); // Set to 0 if no header exists
    };

    // Initial calculation with a slight delay to ensure DOM is ready
    const timer = setTimeout(() => {
      calculateSidebarWidth();
      calculateHeaderHeight();
    }, 100);

    // Recalculate on window resize
    const handleResize = () => {
      calculateSidebarWidth();
      calculateHeaderHeight();
    };

    window.addEventListener('resize', handleResize);

    // Use MutationObserver to detect sidebar changes
    const observer = new MutationObserver(() => {
      calculateSidebarWidth();
      calculateHeaderHeight();
    });

    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style']
    });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const response = datasetTableData?.[0]?.dataset_id || (!isDataSetView && datasetTableData?.[0]?.file_id)
        ? await chatbotHistory(datasetTableData?.[0]?.dataset_id, datasetTableData?.[0]?.file_id, isDataSetView)
        : [];
      setChatHistory(response);
      setMessages(
        chatHistory.flatMap((chat) => [
          {
            id: String(chat.id),
            text: chat.question,
            sender: "user",
            isTyping: false,
            waitingForResponse: false,
          },
          {
            id: String(chat.id),
            text: chat.answer,
            sender: "bot",
            isTyping: false,
            waitingForResponse: false,
            result: chat?.table_data || "",
            chartTemplate: chat?.chart_template || null,
          }
        ])
      );
    };
    fetchData();
  }, [isOpen, datasetTableData, isDataSetView]);

  useEffect(() => {
    const token = sessionStorage.getItem("access_token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setUsername((payload.first_name || payload.last_name) ? (payload.first_name || "") + " " + (payload.last_name || "") : payload?.sub ? payload.sub : "User");
      } catch (error) {
        console.error("Invalid token:", error);
        setUsername("User");
      }
    } else {
      setUsername("User");
    }
  }, []);

  useEffect(() => {
    setChartKey(prev => prev + 1);
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 300);

    return () => clearTimeout(timer);
  }, [isExpanded]);

  const suggestedQuestions = [
    "How many entries are there in the dataset?",
    "Are there any missing values in the dataset?",
    "Give top 5 entries from the dataset",
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleCloseRequest = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
  };

  const normalizeChartTemplate = (template: any) => {
    if (!template) return null;

    // Case 1: Already Apex format → return as is
    if (template.chart && template.series) {
      return template;
    }

    // Case 2: LLM Statistics format → convert to Apex
    if (template.chart_type && template.chart_data) {
      return {
        chart: {
          type: template.chart_type
        },
        series: template.chart_data.series,
        labels: template.chart_data.labels || [],
        title: {
          text: template.title || "Chart"
        }
      };
    }

    return null;
  };

  const exportToCSV = async (chat_id) => {
    const data = await download_csv(Number(chat_id));
    const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "response.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  const generateId = () => {
    return Math.random().toString(36).substring(2, 9);
  };

  const sanitizeText = (text: string): string => {
    if (!text) return "";

    return text
      .replace(/[\u0000-\u0009\u000B-\u000C\u000E-\u001F\u007F-\u009F]/g, "")
      .replace(/[\u2028\u2029]/g, "")
      .replace(/[^\x20-\x7E\n\r\u00A0-\u00FF\u0100-\u017F\u0180-\u024F\u0370-\u03FF\u0400-\u04FF\u0530-\u058F\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u0980-\u09FF\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F\u0D80-\u0DFF\u0E00-\u0E7F\u0E80-\u0EFF\u0F00-\u0FFF\u1000-\u109F\u10A0-\u10FF\u1100-\u11FF\u1200-\u137F\u1380-\u139F\u13A0-\u13FF\u1400-\u167F\u1680-\u169F\u16A0-\u16FF\u1700-\u171F\u1720-\u173F\u1740-\u175F\u1760-\u177F\u1780-\u17FF\u1800-\u18AF\u1900-\u194F\u1950-\u197F\u1980-\u19DF\u19E0-\u19FF\u1A00-\u1A1F\u1A20-\u1AAF\u1B00-\u1B7F\u1B80-\u1BBF\u1BC0-\u1BFF\u1C00-\u1C4F\u1C50-\u1C7F\u1C80-\u1C8F\u1C90-\u1CBF\u1CC0-\u1CCF\u1CD0-\u1CFF\u1D00-\u1D7F\u1D80-\u1DBF\u1DC0-\u1DFF\u1E00-\u1EFF\u1F00-\u1FFF\u2000-\u2021\u2023-\u206F\u2070-\u209F\u20A0-\u20CF\u20D0-\u20FF\u2100-\u214F\u2150-\u218F\u2190-\u21FF\u2200-\u22FF\u2300-\u23FF\u2400-\u243F\u2440-\u245F\u2460-\u24FF\u2500-\u257F\u2580-\u259F\u25A0-\u25FF\u2600-\u26FF\u2700-\u27BF\u27C0-\u27EF\u27F0-\u27FF\u2800-\u28FF\u2900-\u297F\u2980-\u29FF\u2A00-\u2AFF\u2B00-\u2BFF\u2C00-\u2C5F\u2C60-\u2C7F\u2C80-\u2CFF\u2D00-\u2D2F\u2D30-\u2D7F\u2D80-\u2DDF\u2DE0-\u2DFF\u2E00-\u2E7F\u2E80-\u2EFF\u2F00-\u2FDF\u2FF0-\u2FFF\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u3100-\u312F\u3130-\u318F\u3190-\u319F\u31A0-\u31BF\u31C0-\u31EF\u31F0-\u31FF\u3200-\u32FF\u3300-\u33FF\u3400-\u4DBF\u4DC0-\u4DFF\u4E00-\u9FFF\uA000-\uA48F\uA490-\uA4CF\uA4D0-\uA4FF\uA500-\uA63F\uA640-\uA69F\uA6A0-\uA6FF\uA700-\uA71F\uA720-\uA7FF\uA800-\uA82F\uA830-\uA83F\uA840-\uA87F\uA880-\uA8DF\uA8E0-\uA8FF\uA900-\uA92F\uA930-\uA95F\uA960-\uA97F\uA980-\uA9DF\uA9E0-\uA9FF\uAA00-\uAA5F\uAA60-\uAA7F\uAA80-\uAADF\uAAE0-\uAAFF\uAB00-\uAB2F\uAB30-\uAB6F\uAB70-\uABBF\uABC0-\uABFF\uAC00-\uD7AF\uD7B0-\uD7FF\uF900-\uFAFF\uFB00-\uFB4F\uFB50-\uFDFF\uFE00-\uFE0F\uFE10-\uFE1F\uFE20-\uFE2F\uFE30-\uFE4F\uFE50-\uFE6F\uFE70-\uFEFF\uFF00-\uFFEF\uFFF0-\uFFFF]/g, "");
  };

  const tableColumns = (tableHeaders: string[]): ColumnDefinition[] => {
    return tableHeaders.map((header) => ({
      accessorKey: header,
      header: header
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      size: 150,
      Cell: ({ cell }: { cell: any }) => {
        const value = cell.getValue();
        if (typeof value === "boolean") {
          return value ? "True" : "False";
        }
        if (value === null || value === undefined) {
          return "-";
        }
        return value as React.ReactNode;
      },
    }));
  };

  const handleQuestionSelect = (selectedQuestion: string) => {
    setQuestion(selectedQuestion);
    const userId = generateId();
    setMessages((prev) => [
      ...prev,
      { id: userId, text: selectedQuestion, sender: "user" },
    ]);
    handleBotResponse(selectedQuestion);
  };

  const buildChatPayload = (message: string) => {
    const dataset = datasetTableData?.[0];

    if (!dataset?.dataset_id) {
      throw new Error("Dataset ID not found in session storage");
    }

    const payload: chatpayload = {
      question: message,
      dataset_id: dataset.dataset_id,
    };

    if (!isDataSetView) {
      payload.file_id = dataset.file_id;
    }

    return payload;
  };

  const extractResponseData = (response: any, fallbackId: string) => {
    return {
      text:
        "answer" in response
          ? sanitizeText(String(response.answer))
          : "Sorry, I couldn't process your request.",
      table: "result" in response ? response.result : "",
      chart: "chart_template" in response ? response.chart_template : null,
      chatId: "chat_id" in response ? response.chat_id : fallbackId,
      job: response?.raw_data && "async_job" in response?.raw_data
          ? response.raw_data.async_job
          : null,
    };
  };

  const updateMessage = (id: string, updates: any) => (prev: any[]) =>
    prev.map((msg) =>
      msg.id === id ? { ...msg, ...updates } : msg
    );

  const stopTypingWithDelay = (id: string, text: string) => {
    setTimeout(() => {
      setMessages(updateMessage(id, { isTyping: false }));
    }, text.length * 20 + 500);
  };

  const handleBotResponse = async (message: string) => {
    const botResponseId = generateId();
    setIsLoading(true);

    try {
      const chatPayload = buildChatPayload(message);

      // Add placeholder message
      setMessages((prev) => [
        ...prev,
        {
          id: botResponseId,
          text: "",
          sender: "bot",
          waitingForResponse: true,
        },
      ]);

      const response = await chatbotInstruction(chatPayload);
      const { text, table, chart, chatId, job } =
        extractResponseData(response, botResponseId);

      if (job?.job_id) {
        subscribeJob(job.job_type, job.job_id);
      }

      // Update message with response
      setMessages(
        updateMessage(botResponseId, {
          id: String(chatId),
          text,
          waitingForResponse: false,
          isTyping: true,
          ...(table && { result: table }),
          ...(chart && { chartTemplate: chart }),
        })
      );

      // stopTypingWithDelay(botResponseId, text);
    } catch (error) {
      console.error("Error sending message:", error);

      const errorText = sanitizeText(
        "Sorry, there was an error processing your request. Please try again later."
      );

      setMessages(
        updateMessage(botResponseId, {
          text: errorText,
          waitingForResponse: false,
          isTyping: true,
        })
      );

      stopTypingWithDelay(botResponseId, errorText);
    } finally {
      setIsLoading(false);
      setQuestion("");
    }
  };

  const copyTableToClipboard = (data, columns) => {
    if (!data || data.length === 0) return;

    const headers = columns.map(col => col.header).join('\t');
    const rows = data.map(row =>
      columns.map(col => (row[col.accessorKey] ?? '-')).join('\t')
    );
    const tableString = [headers, ...rows].join('\n');
    navigator.clipboard.writeText(tableString)
  };

  const sendMessage = async (message: string) => {
    if (!message.trim()) return;

    const sanitizedMessage = sanitizeText(message.trim());
    if (!sanitizedMessage) return;

    const userId = generateId();
    setMessages((prev) => [
      ...prev,
      { id: userId, text: sanitizedMessage, sender: "user" },
    ]);

    await handleBotResponse(sanitizedMessage);
  };

  const handleSubmit = () => {
    if (!isLoading && question.trim()) {
      sendMessage(question);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

const getChartMeta = (chartTemplate: any, isExpanded: boolean) => {
  const chartType = chartTemplate.chart.type;
  const isPieChart = chartType === "pie" || chartType === "donut";
  const height = isExpanded ? 500 : 350;

  return { chartType, isPieChart, height };
};

const getToolbarConfig = (isPieChart: boolean) => ({
  show: true,
  offsetY: isPieChart ? -20 : -40,
  tools: {
    download: true,
    selection: !isPieChart,
    zoom: !isPieChart,
    zoomin: !isPieChart,
    zoomout: !isPieChart,
    pan: !isPieChart,
    reset: !isPieChart,
  },
});

const getTitleConfig = (title: any, isExpanded: boolean, isPieChart: boolean) => ({
  ...title,
  align: "center",
  margin: isPieChart ? 10 : 20,
  offsetY: isPieChart ? 5 : 20,
  style: {
    fontSize: isExpanded ? "16px" : "10px",
    fontWeight: "bold",
    color: "#333",
  },
});

const getPieOptions = (chartTemplate: any, isExpanded: boolean) => ({
  plotOptions: {
    pie: {
      ...chartTemplate.plotOptions?.pie,
      dataLabels: {
        offset: -10,
        minAngleToShowLabel: 10,
      },
      size: isExpanded ? "80%" : "70%",
    },
  },
  legend: {
    position: "bottom",
    offsetY: 0,
    height: 50,
    fontSize: isExpanded ? "12px" : "10px",
  },
});

const getDataLabels = (isExpanded: boolean) => ({
  enabled: true,
  formatter: (val: number) => {
    if (!isExpanded) {
      const str = val.toFixed(3);
      return str.length > 6 ? str.substring(0, 6) + "..." : str;
    }
    return val.toString();
  },
  style: {
    fontSize: isExpanded ? "12px" : "11px",
    fontWeight: "bold",
    colors: ["#fff"],
  },
});

const getResponsiveConfig = (isExpanded: boolean, isPieChart: boolean) => [
  {
    breakpoint: 600,
    options: {
      chart: {
        height: isExpanded ? 400 : 300,
        toolbar: { offsetY: isPieChart ? -15 : -25 },
      },
      title: {
        offsetY: isPieChart ? 0 : 10,
        style: { fontSize: "12px" },
      },
      dataLabels: { enabled: false },
      legend: { position: "bottom", fontSize: "10px" },
      ...(isPieChart && {
        plotOptions: { pie: { size: "65%" } },
      }),
    },
  },
  {
    breakpoint: 480,
    options: {
      chart: {
        height: isExpanded ? 350 : 250,
        toolbar: {
          offsetY: isPieChart ? -10 : -20,
          show: false,
        },
      },
      title: {
        offsetY: isPieChart ? -5 : 5,
        style: { fontSize: "11px" },
      },
      dataLabels: { enabled: false },
      legend: {
        position: "bottom",
        fontSize: "9px",
        itemMargin: { horizontal: 2, vertical: 2 },
      },
      ...(isPieChart && {
        plotOptions: { pie: { size: "60%" } },
      }),
    },
  },
];

const renderChart = (rawTemplate: any) => {
  const chartTemplate = normalizeChartTemplate(rawTemplate);
  if (!chartTemplate?.chart || !chartTemplate?.series) return null;

  const { chartType, isPieChart, height } = getChartMeta(
    chartTemplate,
    isExpanded
  );

  const options = {
    ...chartTemplate,
    chart: {
      ...chartTemplate.chart,
      height,
      toolbar: getToolbarConfig(isPieChart),
    },
    title: getTitleConfig(chartTemplate.title, isExpanded, isPieChart),
    dataLabels: getDataLabels(isExpanded),
    responsive: getResponsiveConfig(isExpanded, isPieChart),
    ...(isPieChart && getPieOptions(chartTemplate, isExpanded)),
  };

  return (
    <Box
      key={chartKey}
      sx={{
        width: "100%",
        mt: 2,
        mb: 2,
        pt: isPieChart ? 1 : 2,
        "& .apexcharts-canvas": { width: "100% !important" },
        "& .apexcharts-toolbar": { top: "0px !important" },
        ...(isPieChart && {
          "& .apexcharts-pie-label": {
            fill: "#fff",
            fontSize: "11px",
          },
          "& .apexcharts-datalabel": { fill: "#fff" },
        }),
      }}
    >
      <Chart
        options={options}
        series={chartTemplate.series}
        type={chartType}
        height={height}
        width="100%"
      />
    </Box>
  );
};
  return (
    <>
      {!isOpen && (
        <div
          className="position-fixed bottom-0 end-0 p-4"
          style={{ zIndex: 1000 }}
        >
          <button
            onClick={toggleChat}
            className="btn text-white d-flex align-items-center justify-content-center shadow"
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              backgroundColor: "#FD7545",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#b33305ff")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#FD7545")
            }
          >
            <RiRobot2Fill size={24} />
          </button>
        </div>
      )}

      {/* Chat interface */}
      <Box
        sx={{
          position: "fixed",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: "all 0.35s ease",
          boxShadow: 4,
          bgcolor: "#F3F3F8",

          // ---------- COLLAPSED MODE ----------
          ...(!isExpanded && {
            bottom: 30,
            right: 25,
            width: { xs: "90vw", sm: 400 },
            height: { xs: "80vh", sm: 600 },
            borderRadius: 2,
          }),

          // ---------- EXPANDED MODE ----------
          ...(isExpanded && {
            top: headerHeight > 0 ? `${headerHeight}px` : 0,
            left: sidebarWidth > 0 ? `${sidebarWidth}px` : 0,
            width: sidebarWidth > 0 ? `calc(100vw - ${sidebarWidth}px)` : '100vw',
            height: headerHeight > 0 ? `calc(100vh - ${headerHeight}px)` : '100vh',
            borderRadius: 0,
          }),

          visibility: isOpen ? "visible" : "hidden",
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? "translateY(0)" : "translateY(20px)",
        }}
      >

        {/* Header */}
        <div
          className="d-flex justify-content-between align-items-center px-3 py-6 text-white"
          style={{
            background: "linear-gradient(90deg, #FB7545 0%, #FF8C5E 100%)",
            borderTopLeftRadius: isExpanded ? 0 : 12,
            borderTopRightRadius: isExpanded ? 0 : 12,
          }}
        >
          {/* LEFT SIDE */}
          <div className="d-flex align-items-center">

            {/* Circle Icon */}
            <div
              className="d-flex align-items-center justify-content-center me-3"
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.2)",
                fontSize: 22,
              }}
            >
              <RiRobot2Fill />
            </div>

            {/* Title + Subtitle */}
            <div>
              <div className="fw-bold fs-4">AI Chat Agent</div>
              <div style={{ fontSize: 12, opacity: 0.9, fontWeight: "400" }}>
                Always ready to help
              </div>
            </div>
          </div>

          {/* RIGHT SIDE ICONS */}
          <div className="d-flex align-items-center gap-3">

            <button
              onClick={toggleExpand}
            >
              {isExpanded ? <RiCollapseDiagonalLine size={20} /> : <RiExpandDiagonalLine size={20} />}
            </button>

            <button
              onClick={handleCloseRequest}
            >
              <RiCloseLine size={20} />
            </button>

          </div>
        </div>
        <div className="d-flex flex-column h-100 bg-white overflow-hidden">
          <div
            className="flex-grow-1 overflow-auto w-100 p-3"
          >
            {messages.length === 0 ? (
              <div
                style={{
                  maxWidth: "90%",
                  borderRadius: 12,
                  background: "#F7F8FA",     // lighter
                  boxShadow: "none",          // remove heavy shadow
                  padding: "14px 18px",
                }}
              >
                <h5 className="mb-0">
                  Hello! How can I assist you today?
                </h5>
              </div>

            ) : (
              messages.map((msg) => (
                <>
                <div
                  key={String(msg.id)}
                  className={`d-flex mb-3 ${msg.sender === "user"
                    ? "justify-content-end"
                    : "justify-content-start"
                    }`}
                >

                  {/* BOT MESSAGE */}
                  {msg.sender === "bot" && (
                    <>
                      {/* Avatar */}
                      <div
                        className="me-2 mt-2 d-flex align-items-start justify-content-center"
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background: "#E9ECEF",
                          fontSize: 18,
                        }}
                      >
                        <RiRobot2Fill className="mt-2" />
                      </div>

                      {/* Bubble */}
                      <div style={{ maxWidth: "70%" }}>
                        <MessageBubble
                          text={msg.text}
                          sender={msg.sender}
                          isTyping={msg.isTyping}
                          waitingForResponse={msg.waitingForResponse}
                        />
                      </div>
                    </>
                  )}

                  {/* USER MESSAGE */}
                  {msg.sender === "user" && (
                    <>
                      {/* Bubble */}
                      <div style={{ maxWidth: "80%" }}>
                        <MessageBubble
                          text={msg.text}
                          sender={msg.sender}
                          isTyping={msg.isTyping}
                          waitingForResponse={msg.waitingForResponse}
                        />
                      </div>

                      {/* Avatar */}
                      <div
                        className="ms-2 mt-2 d-flex align-items-start justify-content-center"
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background: "#FFD7C8",
                          fontSize: 18,
                        }}
                      >
                        <RiUserFill className="mt-2" />
                      </div>
                    </>
                  )}
                  </div>
                  {/* Charts / Tables */}
                  {msg?.chartTemplate && 
                  <div>{renderChart(msg.chartTemplate)}</div>}

                  {msg?.result &&
                    Array.isArray(msg.result) &&
                    msg.result.some(i => typeof i === "object") &&
                    !msg?.chartTemplate && (

                      <ReusableTable
                        data={msg.result}
                        columns={tableColumns(Object.keys(msg.result[0]))}
                        renderTopToolbarCustomActions={() => (
                          <div className="d-flex justify-content-end gap-2 pe-3">

                            <button
                              className="btn btn-light rounded-circle"
                              onClick={() =>
                                copyTableToClipboard(
                                  msg.result,
                                  tableColumns(Object.keys(msg.result?.[0] ?? {}))
                                )
                              }
                            >
                              <ContentCopyIcon />
                            </button>

                            <button
                              className="btn btn-light rounded-circle"
                              onClick={() => exportToCSV(msg.id)}
                            >
                              <FileDownloadOutlinedIcon />
                            </button>

                          </div>
                        )}
                        enableGlobalFilter={false}
                        enableFullScreenToggle={false}
                        enableDensityToggle={false}
                        enableColumnFilters={false}
                        enableHiding={false}
                        darkBorder={true}
                        enablePagination={false}
                      />
                    )}

                </>
              ))

            )}

            <div ref={messagesEndRef} />
          </div>
          {messages.length === 0 && (
            <div
              className="p-3 border-top d-flex flex-column gap-2"
            >
              <div className="rv-label-muted fw-semibold mb-2">
                Suggested questions
              </div>

              {suggestedQuestions.map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuestionSelect(q)}
                  className="btn text-start"
                  style={{
                    borderRadius: 15,
                    border: "1px solid #F2B39B",
                    background: "#FFF7F3",        // 👈 lighter peach
                    color: "#000000",
                    padding: "10px 16px",
                  }}
                >
                  {q}
                </button>

              ))}
            </div>
          )}
          <div className="border-top" />
          <div className="p-3 bg-white">
            <div className="d-flex align-items-center gap-2">

              <input
                type="text"
                className="form-control"
                placeholder="Type your message..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyPress={handleKeyPress}
                ref={inputRef}
                disabled={isLoading}
                style={{
                  borderRadius: 15,     // square look
                  height: 46,
                }}
              />

              <button
                onClick={handleSubmit}
                disabled={!question.trim() || isLoading}
                className="btn d-flex align-items-center justify-content-center"
                style={{
                  background: "#eb8259ff",
                  color: "white",
                  width: 50,
                  height: 50,
                  borderRadius: 10,
                }}
              >
                <RiSendPlaneFill size={60} />
              </button>
            </div>
          </div>
        </div>
      </Box >
    </>
  );
};

export default ChatBot;